import { useEffect, useMemo, useRef } from 'react';
import { computeLayout } from '@/layout/computeLayout';
import { specForView } from '@/layout/specs';
import type { StageId, ViewId } from '@/layout/types';
import type { DisplayNodes, FunnelResult } from '@/model/types';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useDocumentScrollLock } from '@/hooks/useDocumentScrollLock';
import { usePresence } from '@/hooks/usePresence';
import { useFunnelStore } from '@/store/useFunnelStore';
import { Sankey } from './Sankey/Sankey';

interface Props {
  result: FunnelResult;
  display: DisplayNodes;
}

/** Keep in step with --drawer-anim in index.css. */
const ANIMATION_MS = 240;

const VIEW_COPY: Record<Exclude<ViewId, 'main'>, { title: string; subtitle: string }> = {
  screened: {
    title: 'Screening step',
    subtitle: 'The PHQ-9 threshold, and what sits underneath it',
  },
  followup: {
    title: 'Follow-up step',
    subtitle: 'The chase pool, split by cohort',
  },
};

/**
 * An exploded step opens over the pathway rather than replacing it, so the
 * full funnel stays on screen as context for the step being examined.
 */
export function ExplodedModal({ result, display }: Props) {
  const view = useFunnelStore((s) => s.view);
  const setView = useFunnelStore((s) => s.setView);
  const selectedStage = useFunnelStore((s) => s.selectedStage);
  const openStage = useFunnelStore((s) => s.openStage);

  const open = view !== 'main';
  const { mounted, active } = usePresence(open, ANIMATION_MS);
  // Stay locked through the exit transition, not merely until `open` flips.
  useDocumentScrollLock(mounted);

  /* Hold the last exploded view so it still has content to draw on the way out. */
  const lastView = useRef<Exclude<ViewId, 'main'>>('screened');
  if (view !== 'main') lastView.current = view;
  const shown = lastView.current;

  const panelRef = useRef<HTMLDivElement>(null);
  /* The drawer can open on top of this; only one of them should trap focus. */
  useFocusTrap(panelRef, open && mounted && selectedStage === null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape belongs to the drawer while it is open; this closes on the next press.
      if (useFunnelStore.getState().selectedStage !== null) return;
      setView('main');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setView]);

  const spec = useMemo(() => specForView(shown, result, display), [shown, result, display]);
  const layout = useMemo(() => computeLayout(spec), [spec]);

  if (!mounted) return null;
  const copy = VIEW_COPY[shown];
  const openClass = active ? ' is-open' : '';

  const activate = ({ stage }: { stage?: StageId; explode?: ViewId }) => {
    if (!stage) return;
    openStage(stage, {
      completed: result.nodes.completed,
      screeningRate: result.rates.screeningRate,
    });
  };

  return (
    <>
      <div
        className={`modal-backdrop${openClass}`}
        onClick={() => setView('main')}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className={`exploded-modal${openClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exploded-title"
        tabIndex={-1}
      >
        <header className="modal-header">
          <div>
            <h2 id="exploded-title">{copy.title}</h2>
            <p className="modal-subtitle">{copy.subtitle}</p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={() => setView('main')}
            aria-label="Close exploded view"
          >
            ×
          </button>
        </header>

        <div className="modal-body">
          <div className="flow-frame">
            <Sankey
              layout={layout}
              selectedStage={selectedStage}
              selectable={spec.selectable !== false}
              onActivate={activate}
            />
          </div>
        </div>
      </div>
    </>
  );
}
